<template>
  <main>
    <b-overlay :show="loading">
      <form-wizard
        color="#ff9801"
        :title="null"
        :subtitle="null"
        finish-button-text="Simpan"
        back-button-text="Sebelumnya"
        next-button-text="Selanjutnya"
        @on-complete="formSubmitted"
      >
        <tab-content title="Daftar Barang">
          <validation-observer ref="formBarang">
            <b-card class="mb-2">
              <h6 class="mb-1">Tambah Barang</h6>
              <b-row class="mb-1">
                <b-col cols="12">
                  <b-form-checkbox
                    v-model="dariPembelian"
                    @change="onToggleDariPembelian"
                  >
                    Dari Pembelian
                  </b-form-checkbox>
                </b-col>
                <b-col v-if="dariPembelian" cols="12" md="6">
                  <b-form-group label="Pilih Kode Pembelian" class="mb-1">
                    <v-select
                      v-model="selectedPembelianId"
                      :options="pembelianOptions"
                      label="text"
                      :reduce="(option) => option.value"
                      placeholder="Cari kode pembelian..."
                      @input="onPembelianSelected"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row class="align-items-end">
                <b-col cols="12" md="4">
                  <b-form-group label="Pilih Barang" class="mb-1">
                    <validation-provider
                      #default="{ errors }"
                      name="barang"
                      rules="required"
                    >
                      <v-select
                        v-model="formBarang.barang_id"
                        :reduce="(option) => option.value"
                        :options="barangOptionsFiltered"
                        label="text"
                        :disabled="dariPembelian && !selectedPembelianId"
                        placeholder="Cari barang..."
                      />
                      <small v-if="dariPembelian && !selectedPembelianId" class="text-muted">
                        Pilih kode pembelian terlebih dahulu
                      </small>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="2">
                  <b-form-group label="Jumlah" class="mb-1">
                    <validation-provider
                      #default="{ errors }"
                      name="nilai"
                      rules="required|positive"
                    >
                      <b-form-input
                        v-model="formBarang.nilai"
                        type="number"
                        :state="errors.length > 0 ? false : null"
                        @input="recalcKonversi"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="3">
                  <b-form-group label="Satuan Konversi" class="mb-1">
                    <v-select
                      v-model="formBarang.selectedKonversiId"
                      :reduce="(option) => option.value"
                      :options="konversiOptions"
                      label="text"
                      :disabled="konversiOptions.length < 1"
                      placeholder="pcs"
                    />
                    <small v-if="konversiOptions.length < 1" class="text-muted">
                      Default pcs
                    </small>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="3">
                  <b-form-group label="Hasil Konversi" class="mb-1">
                    <b-input-group>
                      <b-form-input
                        :value="formBarang.jumlah"
                        type="number"
                        readonly
                        disabled
                      />
                      <b-input-group-append>
                        <b-button variant="outline-secondary" disabled>
                          {{ satuanTerkecilLabel }}
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row class="align-items-end">
                <b-col cols="12" md="4">
                  <b-form-group label="No Container" class="mb-1">
                    <b-form-input
                      v-model="formBarang.no_container"
                      placeholder="Opsional"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                  <b-form-group label="No Seal" class="mb-1">
                    <b-form-input
                      v-model="formBarang.no_seal"
                      placeholder="Opsional"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4" class="mb-1">
                  <b-button
                    variant="primary"
                    @click.prevent="addBarang"
                  >
                    <feather-icon icon="PlusIcon" class="mr-50" />
                    Tambah
                  </b-button>
                </b-col>
              </b-row>
            </b-card>

            <b-card>
              <h6 class="mb-1">Barang Ditambahkan</h6>
              <b-table
                striped
                hover
                responsive
                :fields="tableFields"
                :items="selectedBarangs"
              >
                <template #cell(no)="row">
                  <strong>{{ row.index + 1 }}</strong>
                </template>
                <template #cell(nama)="{ item }">
                  <div>{{ item.nama || "-" }}</div>
                  <small class="d-block text-muted">
                    No Container: {{ item.no_container || "-" }}
                  </small>
                  <small class="d-block text-muted">
                    No Seal: {{ item.no_seal || "-" }}
                  </small>
                  <small
                    v-if="item.dari_po || item.pembelian_rincian_id"
                    class="d-block text-muted"
                  >
                    Kode PO: {{ item.dari_po || "-" }}
                  </small>
                </template>
                <template #cell(qty_pembelian)="{ item }">
                  <span>{{ item.qty_pembelian != null ? item.qty_pembelian : "-" }}</span>
                  <b-badge
                    v-if="item.qty_pembelian != null && item.jumlah != item.qty_pembelian"
                    variant="warning"
                    class="ml-50"
                  >
                    Berbeda
                  </b-badge>
                </template>
                <template #cell(actions)="row">
                  <b-button
                    v-b-tooltip.hover.right="'Urungkan'"
                    size="sm"
                    variant="danger"
                    @click.prevent="removeBarang(row)"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </template>
              </b-table>
              <p
                v-if="selectedBarangs.length < 1"
                class="text-muted mb-0 text-center py-1"
              >
                Belum ada barang. Tambahkan dari form di atas.
              </p>
            </b-card>
          </validation-observer>
        </tab-content>
      </form-wizard>
    </b-overlay>
  </main>
</template>
<script>
import { FormWizard, TabContent } from "vue-form-wizard";
import "vue-form-wizard/dist/vue-form-wizard.min.css";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import {
  BRow,
  BBadge,
  BInputGroupAppend,
  BCol,
  BCard,
  BOverlay,
  BButton,
  BTable,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BFormCheckbox,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { extend } from "vee-validate";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "vee-validate/dist/rules";

extend("required", {
  ...required,
  message: "This field is required",
});
extend("positive", (value) => {
  return value > 0;
});

export default {
  components: {
    BBadge,
    ValidationObserver,
    ValidationProvider,
    FormWizard,
    TabContent,
    BRow,
    BOverlay,
    BTable,
    BCard,
    BButton,
    BCol,
    BFormGroup,
    BFormInput,
    vSelect,
    BInputGroupAppend,
    BInputGroup,
    BFormCheckbox,
    ToastificationContent,
  },
  computed: {
    satuanTerkecilLabel() {
      if (this.idb && this.idb.satuan && this.idb.satuan.satuan) {
        return this.idb.satuan.satuan;
      }
      return "pcs";
    },
    pembelianOptions() {
      return (this.pembelianList || []).map((p) => ({
        value: p.id,
        text: `${p.no} (${p.total_item != null ? p.total_item : 0} item)`,
      }));
    },
    barangOptionsFiltered() {
      if (this.dariPembelian && this.pembelianRincians.length > 0) {
        return this.pembelianRincians.map((r) => {
          const fromStore = this.$store.getters["barang/getBarang"](r.id_barang);
          const nama = r.barang
            ? r.barang.nama
            : (fromStore && fromStore.nama) || "-";
          const satuan =
            r.barang && r.barang.satuan
              ? r.barang.satuan.satuan
              : "";
          return {
            value: r.id_barang,
            text: satuan ? `${nama} - ${satuan} (PO: ${r.qty})` : `${nama} (PO: ${r.qty})`,
            rincianId: r.id,
            qty: r.qty,
          };
        });
      }
      return this.barangOptions;
    },
  },
  watch: {
    "formBarang.barang_id"(barang_id) {
      if (barang_id) {
        this.loadKonversiForBarang(barang_id);
        this.getIDBarang(barang_id);
        if (this.dariPembelian && this.pembelianRincians.length > 0) {
          this.applyPembelianBarangSelection(barang_id);
        }
      } else {
        this.konversiOptions = [];
        this.konversiList = [];
        this.formBarang.selectedKonversiId = null;
        this.formBarang.nilaiKonversi = 1;
        this.recalcKonversi();
      }
    },
    "formBarang.selectedKonversiId"() {
      this.recalcKonversi();
    },
  },
  methods: {
    applyPembelianBarangSelection(barang_id) {
      const rincian = this.pembelianRincians.find(
        (r) => String(r.id_barang) === String(barang_id)
      );
      if (!rincian) return;
      this.formBarang.pembelian_rincian_id = rincian.id;
      this.formBarang.qty_pembelian = rincian.qty;
      this.formBarang.nilai = rincian.qty;
      if (rincian.no_container) {
        this.formBarang.no_container = rincian.no_container;
      }
      if (rincian.no_seal) {
        this.formBarang.no_seal = rincian.no_seal;
      }
      this.$nextTick(() => {
        this.recalcKonversi();
      });
    },
    async loadPembelian() {
      this.loading = true;
      try {
        const list = await this.$store.dispatch("pembelian/getOptions", {
          length: 500,
          order: "desc",
        });
        this.pembelianList = Array.isArray(list) ? list : [];
      } catch (e) {
        this.displayError(e);
        this.pembelianList = [];
      } finally {
        this.loading = false;
      }
    },
    mergePembelianRincianToTable(rincians, poNo) {
      let added = 0;
      for (const r of rincians) {
        const already = this.selectedBarangs.some(
          (b) =>
            b.pembelian_rincian_id != null &&
            String(b.pembelian_rincian_id) === String(r.id)
        );
        if (already) continue;

        const fromStore = this.$store.getters["barang/getBarang"](r.id_barang);
        const barang = r.barang || fromStore;
        if (!barang) continue;

        // Qty PO langsung sebagai pcs — jangan hit konversi (N AJAX)
        const qtyNum = parseFloat(r.qty);
        const jumlah =
          Number.isFinite(qtyNum) && qtyNum > 0 ? Math.floor(qtyNum) : 0;

        this.selectedBarangs.push({
          barang_id: barang.id || r.id_barang,
          nama: barang.nama || "-",
          jumlah,
          satuan: barang.satuan ? barang.satuan.satuan : "pcs",
          no_container: r.no_container || null,
          no_seal: r.no_seal || null,
          dari_po: poNo || null,
          pembelian_rincian_id: r.id,
          qty_pembelian: r.qty,
        });
        added += 1;
      }
      return added;
    },
    async onPembelianSelected(id) {
      if (!id) {
        this.pembelianRincians = [];
        this.selectedPembelianNo = null;
        this.resetFormBarang();
        return;
      }
      const po = this.pembelianList.find((p) => String(p.id) === String(id));
      this.selectedPembelianNo = po ? po.no : null;
      this.loading = true;
      try {
        const rincians = await this.$store.dispatch(
          "pembelian-rincian/getData",
          { id_pembelian: id }
        );
        this.pembelianRincians = Array.isArray(rincians) ? rincians : [];
        if (this.pembelianRincians.length < 1) {
          this.displayError({
            message: "Pembelian ini tidak memiliki rincian barang",
          });
          this.resetFormBarang();
          return;
        }
        const added = this.mergePembelianRincianToTable(
          this.pembelianRincians,
          this.selectedPembelianNo
        );
        if (added > 0) {
          this.displaySuccess({
            message: `${added} barang dari PO ditambahkan ke daftar`,
          });
        } else {
          this.displayInfo({
            message: "Semua rincian PO sudah ada di daftar",
          });
        }
        this.resetFormBarang();
      } catch (e) {
        this.pembelianRincians = [];
        this.displayError(e);
      } finally {
        this.loading = false;
      }
    },
    async onToggleDariPembelian(val) {
      if (!val) {
        this.selectedPembelianId = null;
        this.pembelianRincians = [];
        this.selectedPembelianNo = null;
        this.resetFormBarang();
      } else {
        await this.loadPembelian();
      }
    },
    getSelectedKonversi() {
      if (!this.konversiList || this.konversiList.length < 1) return null;
      const id = this.formBarang.selectedKonversiId;
      if (id == null || id === "") return this.konversiList[0];
      return (
        this.konversiList.find(
          (k) => String(k.id) === String(id) || String(k.id_satuan) === String(id)
        ) || this.konversiList[0]
      );
    },
    recalcKonversi() {
      const qty = parseFloat(this.formBarang.nilai);
      const selected = this.getSelectedKonversi();
      const nilai = selected
        ? parseFloat(selected.nilai)
        : parseFloat(this.formBarang.nilaiKonversi) || 1;
      let converted = 0;
      if (Number.isFinite(qty) && qty > 0 && Number.isFinite(nilai) && nilai > 0) {
        converted = Math.floor(qty * nilai);
      } else if (Number.isFinite(qty) && qty > 0 && (!this.konversiList || this.konversiList.length < 1)) {
        // No konversi data — treat input as pcs/base unit
        converted = Math.floor(qty);
      }
      this.formBarang.jumlah = converted;
      this.formBarang.nilaiKonversi = Number.isFinite(nilai) ? nilai : 1;
    },
    async loadKonversiForBarang(barang_id) {
      const konvs = await this.$store.dispatch("konversi/getData", {
        id_barang: barang_id,
      });
      this.konversiList = Array.isArray(konvs) ? konvs : [];
      this.konversiOptions = this.konversiList.map((konv) => ({
        value: konv.id != null ? konv.id : konv.id_satuan,
        text: konv.satuan && konv.satuan.satuan ? konv.satuan.satuan : "pcs",
      }));
      // Default first option (pcs / first satuan)
      if (this.konversiOptions.length > 0) {
        this.formBarang.selectedKonversiId = this.konversiOptions[0].value;
        const first = this.konversiList[0];
        this.formBarang.nilaiKonversi = first ? parseFloat(first.nilai) || 1 : 1;
      } else {
        this.formBarang.selectedKonversiId = null;
        this.formBarang.nilaiKonversi = 1;
      }
      this.recalcKonversi();
    },
    async getIDBarang(barang_id) {
      const idbar = await this.$store.dispatch("barang/getDataById", barang_id);
      this.idb = idbar || {};
    },
    removeBarang(row) {
      const { item, index } = row;
      // Baris baru dari PO (belum tersimpan) cukup dihapus lokal
      if (!item.id) {
        this.selectedBarangs.splice(index, 1);
        return;
      }
      this.$store
        .dispatch("penerimaan-barang/saveBarang", [
          {
            id: item.id,
            fungsi: 2,
          },
        ])
        .then(() => this.selectedBarangs.splice(index, 1))
        .catch((e) => this.displayError(e));
    },
    addBarang() {
      this.$refs.formBarang.validate().then((success) => {
        if (!success) return;
        this.recalcKonversi();
        if (!this.formBarang.jumlah || this.formBarang.jumlah < 1) {
          this.displayError({
            message: "Hasil konversi harus lebih dari 0",
          });
          return false;
        }
        const barang =
          this.$store.getters["barang/getBarang"](this.formBarang.barang_id) ||
          (this.pembelianRincians.find(
            (r) => String(r.id_barang) === String(this.formBarang.barang_id)
          ) || {}).barang;
        if (!barang) {
          this.displayError({
            message: "Barang tidak ditemukan, harap pilih barang lain",
          });
          return false;
        }

        this.selectedBarangs.push({
          barang_id: barang.id,
          nama: barang.nama,
          jumlah: this.formBarang.jumlah,
          satuan: barang.satuan ? barang.satuan.satuan : "-",
          no_container: this.formBarang.no_container,
          no_seal: this.formBarang.no_seal,
          dari_po: this.selectedPembelianNo || null,
          pembelian_rincian_id: this.formBarang.pembelian_rincian_id || null,
          qty_pembelian: this.formBarang.qty_pembelian || null,
        });

        this.resetFormBarang();
      });
    },
    resetFormBarang() {
      this.formBarang = {
        penerimaan_id: null,
        barang_id: null,
        no_container: null,
        no_seal: null,
        harga: 1,
        jumlah: 0,
        nilai: null,
        selectedKonversiId: null,
        nilaiKonversi: 1,
        pembelian_rincian_id: null,
        qty_pembelian: null,
      };
      this.konversiOptions = [];
      this.konversiList = [];
      this.idb = {};
    },
    async getBarang() {
      const barangs = await this.$store.dispatch("barang/getData");
      this.barangOptions = [];
      barangs.map((barang) => {
        const name = barang.satuan
          ? `${barang.nama} - ${barang.satuan.satuan}`
          : barang.nama;
        this.barangOptions.push({
          value: barang.id,
          text: name,
        });
      });

      if (this.$route.params.id) {
        this.getPenerimaanBarang();
      }
    },
    getPenerimaanBarang() {
      this.$store
        .dispatch("penerimaan-barang/getDataBarang", {
          penerimaan_id: this.$route.params.id,
          order: "desc",
        })
        .then(async (barangsPenerimaan) => {
          await this.loadPembelian();
          barangsPenerimaan.map((bp) => {
            const barang_id = bp.barang ? bp.barang.id : null;
            bp.barang_id = barang_id;
            const barang = this.$store.getters["barang/getBarang"](barang_id);
            bp.nama = barang
              ? barang.nama
              : bp.barang
                ? bp.barang.nama
                : "-";
            bp.satuan =
              barang && barang.satuan
                ? barang.satuan.satuan
                : bp.barang && bp.barang.satuan
                  ? bp.barang.satuan.satuan
                  : "-";
            if (bp.pembelian_rincian) {
              bp.pembelian_rincian_id = bp.pembelian_rincian.id;
              bp.qty_pembelian = bp.pembelian_rincian.qty;
              const po = this.pembelianList.find(
                (p) => String(p.id) === String(bp.pembelian_rincian.id_pembelian)
              );
              bp.dari_po = po ? po.no : "-";
            } else {
              bp.dari_po = null;
              bp.qty_pembelian = null;
            }
          });
          this.selectedBarangs = barangsPenerimaan;
        });
    },
    async formSubmitted() {
      if (this.selectedBarangs.length < 1) {
        this.displayError({
          message: "Harap tambahkan minimal 1 barang",
        });
        return false;
      }

      try {
        if (this.id) {
          this.form.id = this.id;
        }
        const barangPenerimaan = this.selectedBarangs.map((bp) => {
          const base = {
            penerimaan_id: this.$route.params.id,
            barang_id: bp.barang_id,
            harga: 1,
            jumlah: bp.jumlah,
            no_container: bp.no_container,
            no_seal: bp.no_seal,
            pembelian_rincian_id: bp.pembelian_rincian_id ?? null,
          };
          if (bp.id) {
            return { id: bp.id, ...base };
          }
          return base;
        });
        this.loading = true;

        await this.$store.dispatch(
          "penerimaan-barang/saveBarang",
          barangPenerimaan
        );
        const message = this.$route.params.id
          ? "Barang Penerimaan Berhasil Diperbaharui!"
          : "Barang Penerimaan Berhasil Ditambahkan!";
        this.displaySuccess({
          message,
        });
        setTimeout(() => {
          this.loading = false;
          this.$router.push({
            name: "penerimaan-barang-view",
            params: { id: this.$route.params.id },
          });
        }, 1000);
      } catch (e) {
        this.loading = false;
        this.displayError(e);
        return false;
      }
    },
  },
  data() {
    return {
      idb: {},
      loading: false,
      selectedBarangs: [],
      form: {
        gudang_id: null,
        no_invoice: null,
        jenis: 0,
        tanggal_invoice: null,
        tanggal_penerimaan: null,
        keterangan: null,
      },
      formBarang: {
        penerimaan_id: null,
        barang_id: null,
        no_container: null,
        no_seal: null,
        harga: 1,
        jumlah: 0,
        nilai: null,
        selectedKonversiId: null,
        nilaiKonversi: 1,
        pembelian_rincian_id: null,
        qty_pembelian: null,
      },
      konversiList: [],
      konversiOptions: [],
      dariPembelian: false,
      pembelianList: [],
      selectedPembelianId: null,
      selectedPembelianNo: null,
      pembelianRincians: [],
      tableFields: [
        { key: "no", label: "No" },
        { key: "nama", label: "Nama Barang" },
        { key: "qty_pembelian", label: "Qty PO" },
        { key: "jumlah", label: "Kuantitas" },
        { key: "satuan", label: "Satuan" },
        { key: "actions", label: "#" },
      ],
      id: null,
      barangOptions: [],
    };
  },
  mounted() {
    this.getBarang();
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-wizard.scss";
@import "@core/scss/vue/libs/vue-select.scss";
</style>
